import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useKraCycleStore from '../../store/kraCycleStore';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import KRACycleNotes from '../../components/KRACycleNotes';
import ScrollX from '../../components/ScrollX';
import { ownScoreOutOf100 } from '../../utils/kraScore';

const ScoreSelector = ({ value, onChange, disabled }) => (
  <div className="flex gap-1">
    {[0, 1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={disabled}
        onClick={() => onChange(n)}
        className={`w-7 h-7 rounded text-xs font-bold border ${
          value === n
            ? 'bg-komal-blue text-white border-komal-blue'
            : 'bg-white text-gray-500 border-gray-300 hover:border-komal-blue'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {n}
      </button>
    ))}
  </div>
);

const MyKRACycleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { selectedCycle, isLoading, isSubmitting, fetchCycle, saveSelfResponses, submitSelfResponses } = useKraCycleStore();
  const showToast = useUIStore((s) => s.showToast);
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState({});

  useEffect(() => {
    fetchCycle(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!selectedCycle) return;
    const s = {};
    const c = {};
    selectedCycle.items.forEach((item) => {
      s[item.kra_item_id] = item.self_score;
      c[item.kra_item_id] = item.self_comment || '';
    });
    setScores(s);
    setComments(c);
  }, [selectedCycle]);

  if (isLoading || !selectedCycle) {
    return <div className="min-h-screen p-6"><p className="text-gray-500">Loading...</p></div>;
  }

  const isOwner = selectedCycle.employee_id === user?.id;
  // Self follows the form as scores are picked; the HOD's own total is only
  // shown once the review is locked (same point the Final Score appears).
  const selfTotal = ownScoreOutOf100(selectedCycle.items, (item) => (item.kra_item_id in scores ? scores[item.kra_item_id] : item.self_score));
  const hodTotal = selectedCycle.status === 'locked'
    ? ownScoreOutOf100(selectedCycle.items, (item) => item.hod_score)
    : null;
  const editable = isOwner && ['draft', 'reopened'].includes(selectedCycle.status);

  const buildResponses = () =>
    selectedCycle.items.map((item) => ({
      kra_item_id: item.kra_item_id,
      score: scores[item.kra_item_id] ?? null,
      comment: comments[item.kra_item_id] || null,
    }));

  const handleSaveDraft = async () => {
    const result = await saveSelfResponses(id, buildResponses());
    showToast(result.success ? 'Draft saved' : result.error, result.success ? 'success' : 'error');
  };

  const handleSubmit = async () => {
    await saveSelfResponses(id, buildResponses());
    const result = await submitSelfResponses(id);
    if (result.success) {
      showToast('Self-evaluation submitted to HOD', 'success');
      navigate('/kra');
    } else {
      showToast(result.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">
              My KRA — {new Date(selectedCycle.month).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{selectedCycle.employee_name}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Self = your own scores only (live as you pick them); HOD = their
              scores only, shown once the review is locked. Both used to show the
              blended preview/final total, which moved when the HOD scored. */}
          <div className="glass-card p-4 border-l-4 border-komal-blue">
            <div className="text-2xl font-bold text-gray-900">{selfTotal ?? '—'} <span className="text-sm font-normal text-gray-400">/ 100</span></div>
            <div className="text-xs text-gray-500 mt-1">Self Score (live)</div>
          </div>
          <div className="glass-card p-4 border-l-4 border-gray-300">
            <div className="text-2xl font-bold text-gray-400">{hodTotal ?? '—'}{hodTotal != null && <span className="text-sm font-normal text-gray-400"> / 100</span>}</div>
            <div className="text-xs text-gray-500 mt-1">HOD Score {hodTotal == null ? '· pending' : ''}</div>
          </div>
          <div className="glass-card p-4 border-l-4 border-amber-500">
            <div className="text-2xl font-bold text-gray-900">{selectedCycle.external_inputs_received}/{selectedCycle.external_inputs_required}</div>
            <div className="text-xs text-gray-500 mt-1">External Inputs Received</div>
          </div>
          <div className="glass-card p-4 border-l-4 border-gray-300">
            {selectedCycle.status === 'locked' && selectedCycle.final_score != null ? (
              <div className="text-2xl font-bold text-gray-900">{selectedCycle.final_score} <span className="text-sm font-normal text-gray-400">/ 100</span></div>
            ) : (
              <div className="text-sm font-semibold text-gray-500">Pending HOD Review</div>
            )}
            <div className="text-xs text-gray-500 mt-1">Final Score</div>
          </div>
        </div>

        <div className="glass-card overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">KRA Details</h2>
            <p className="text-xs text-gray-500 mt-0.5">Objective data is read-only where another department owns it.</p>
          </div>
          <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-5 py-2 font-medium">KRA</th>
                  <th className="px-5 py-2 font-medium">Weight</th>
                  <th className="px-5 py-2 font-medium">Evidence</th>
                  <th className="px-5 py-2 font-medium">Self (0–5)</th>
                  <th className="px-5 py-2 font-medium">HOD</th>
                </tr>
              </thead>
              <tbody>
                {selectedCycle.items.map((item) => (
                  <tr key={item.kra_item_id} className="border-b border-gray-50 last:border-0 align-top">
                    <td className="px-5 py-3">
                      <div className="font-medium text-gray-900">{item.name}</div>
                      <div className="text-xs text-gray-500">{item.kpi}</div>
                    </td>
                    <td className="px-5 py-3 font-semibold">{item.weightage}%</td>
                    <td className="px-5 py-3 text-xs text-gray-500 max-w-[180px]">{item.evidence_source}</td>
                    <td className="px-5 py-3">
                      <ScoreSelector
                        value={scores[item.kra_item_id]}
                        disabled={!editable}
                        onChange={(n) => setScores((s) => ({ ...s, [item.kra_item_id]: n }))}
                      />
                      {editable && (
                        <input
                          type="text"
                          placeholder="Comment (optional)"
                          value={comments[item.kra_item_id] || ''}
                          onChange={(e) => setComments((c) => ({ ...c, [item.kra_item_id]: e.target.value }))}
                          className="mt-2 w-full text-xs border border-gray-200 rounded px-2 py-1"
                        />
                      )}
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-400 italic">
                      {item.hod_score != null ? `${item.hod_score}/5` : 'Pending'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>

          {editable && (
            <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={handleSaveDraft} disabled={isSubmitting} className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 font-semibold disabled:opacity-60">
                Save Draft
              </button>
              <button onClick={handleSubmit} disabled={isSubmitting} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
                Submit to HOD
              </button>
            </div>
          )}
          {!editable && (
            <div className="px-5 py-3 border-t border-gray-200 text-xs text-gray-400">
              This record is {selectedCycle.status === 'locked' ? 'locked' : 'not editable by you right now'} — reopening needs your HOD or HR, and is logged.
            </div>
          )}
        </div>

        <div className="mt-6">
          <KRACycleNotes cycleId={id} isOwnCycle={isOwner} />
        </div>
      </div>
    </div>
  );
};

export default MyKRACycleDetailPage;
